import { useEffect, useRef, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExcelBtn } from "@/lib/excelDownload";
import { modal } from "@/lib/modal";
import { copiarTexto } from "@/lib/clipboard";
import { inicioDiaBR, fimDiaBR, atalhoPeriodoBR } from "@/lib/periodoBR";
import { comPagina, normalizaPaginado } from "@/lib/paginacao";
import Pagination from "@/components/crud/Pagination";
import { qrPngDataUrl, baixarQrPng, baixarQrSvg, imprimirCartaz } from "@/lib/qrCode";
import {
  urlPublicaCompleta, nomeArquivoQr, VARIAVEIS_TEXTO, CATEGORIAS_LOG, CATEGORIA_LOG_LBL,
  EVENTOS_PADRAO, fmtDataHoraBR,
} from "@/lib/indicacaoWeb";
import logo from "@/assets/logo-grupo.png";
import { Link } from "react-router-dom";
import { QrCode, Copy, ExternalLink, Download, Printer, MessageCircle, ScrollText, AlertTriangle, ShieldCheck, MessagesSquare } from "lucide-react";

/**
 * Fase 4 (pedido de 19/10, item 9) — Master > Gestão > Indicação pela web.
 * Liga/desliga a página pública (master_config.indicacao_web_ativa, padrão desligada), link
 * público e QR Code de cada unidade (gerados aqui no navegador: PNG, SVG e cartaz para impressão)
 * e os registros da página com os filtros das outras telas (unidade do cabeçalho, categoria,
 * período, evento e resultado), paginação e Excel. O texto do wa.me por unidade saiu daqui na
 * Fase 5: fica em Sistema > Mensagens automáticas (link em cada unidade).
 * Contrato: GET/PUT /master/indicacao-web/config e GET /master/indicacao-web/logs (+ /excel).
 */

const ATALHOS = [["hoje", "Hoje"], ["ontem", "Ontem"], ["7d", "7 dias"], ["30d", "30 dias"], ["mes", "Este mês"]];
const RESULTADOS = [["all", "Todos"], ["true", "Sucesso"], ["false", "Falha"]];
const inputCls = "mt-1 w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterIndicacaoWeb() {
  const { unitId } = useUnit();
  const [cfg, setCfg] = useState(null);             // resposta do GET /config
  const [erroCfg, setErroCfg] = useState("");
  const [salvandoToggle, setSalvandoToggle] = useState(false);
  const [qrs, setQrs] = useState({});               // {unit_id: data URL da prévia}

  const inicial = atalhoPeriodoBR("hoje");
  const [inicio, setInicio] = useState(inicial.inicio);
  const [fim, setFim] = useState(inicial.fim);
  const [categoria, setCategoria] = useState("all");
  const [evento, setEvento] = useState("all");
  const [resultado, setResultado] = useState("all");
  const [logs, setLogs] = useState({ items: [], total: 0, page: 1, total_pages: 0, totais_por_evento: {}, eventos: null });

  const unitParam = unitId && unitId !== "all" ? unitId : null;
  const ini = inicio ? inicioDiaBR(inicio) : null;
  const fi = fim ? fimDiaBR(fim) : null;
  const periodoValido = !!(ini && fi && inicio <= fim);
  const atalhoAtivo = (ATALHOS.find(([k]) => atalhoPeriodoBR(k).inicio === inicio && atalhoPeriodoBR(k).fim === fim) || [null])[0];
  const origem = typeof window !== "undefined" ? window.location.origin : "";

  const carregarConfig = async () => {
    try {
      const r = await masterApi.get("/master/indicacao-web/config");
      const d = r.data || {};
      setCfg(d);
      setErroCfg("");
    } catch (e) { setErroCfg(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarConfig(); }, []);

  const unidades = Array.isArray(cfg?.unidades) ? cfg.unidades : [];
  const visiveis = unitParam ? unidades.filter((u) => u.unit_id === unitParam) : unidades;
  const urlDe = (u) => urlPublicaCompleta(u.url_publica || `/indicacao/${u.slug}`, origem);

  // Prévia dos QR Codes (gerados no navegador).
  const chaveQr = visiveis.map((u) => `${u.unit_id}|${urlDe(u)}`).join(",");
  useEffect(() => {
    let vivo = true;
    visiveis.forEach((u) => {
      const url = urlDe(u);
      if (!url) return;
      Promise.resolve(qrPngDataUrl(url, 360))
        .then((png) => { if (vivo) setQrs((q) => ({ ...q, [u.unit_id]: png })); })
        .catch(() => {});
    });
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveQr]);

  const alternar = async (proximo) => {
    // Servidor sem o IP real dos visitantes e sem o Turnstile: o backend recusa ligar (409).
    // Avisa antes, com o motivo vindo do servidor (desligar sempre passa).
    if (proximo && cfg?.pode_ligar === false) {
      modal.error(cfg.aviso_risco || "Para ligar a página neste servidor, configure antes a verificação de segurança (Turnstile).");
      return;
    }
    const ok = await modal.confirm({
      title: proximo ? "Ligar a indicação pela web" : "Desligar a indicação pela web",
      message: proximo
        ? "Com a página ligada, quem já tem cadastro (Cliente Normal ou Motorista de App, de qualquer unidade) informa a placa, sem código de confirmação, e vê na tela o primeiro nome e os próprios códigos de indicação ativos, com o botão Encaminhar no WhatsApp, e pode gerar um lote novo (lote de 3, novo lote só com no máximo 3 ativos); ao gerar, a cópia vai para o celular do cadastro. Atenção: quem souber a placa vê esses códigos. Quem não tem cadastro pode se cadastrar pela página (categoria Normal); o cadastro só é criado depois do código de confirmação enviado ao celular. Depois disso, o caminho é o mesmo. O link ou QR Code de qualquer unidade atende clientes de todas, e os códigos valem em qualquer unidade. Cada mensagem da página consome crédito de WhatsApp ou SMS."
        : "Com a página desligada, os links e QR Codes de todas as unidades passam a mostrar só um aviso de página indisponível. Os códigos já gerados continuam valendo no posto.",
      details: [
        { label: "Situação atual", value: cfg?.ativa ? "LIGADA" : "DESLIGADA" },
        { label: "Passa a ficar", value: proximo ? "LIGADA" : "DESLIGADA" },
        { label: "Alcance", value: "Todas as unidades" },
        { label: "Limite de SMS da página", value: cfg?.teto_sms_dia != null ? `${cfg.teto_sms_dia} por dia` : "padrão do servidor" },
      ],
      confirmText: proximo ? "Ligar a página" : "Desligar a página",
      tone: proximo ? undefined : "danger",
    });
    if (!ok) return;
    setSalvandoToggle(true);
    try {
      const r = await masterApi.put("/master/indicacao-web/config", { ativa: proximo });
      // Atualiza só a situação e os dados do servidor.
      setCfg((c) => ({ ...(c || {}), ...(r.data || {}), ativa: typeof r?.data?.ativa === "boolean" ? r.data.ativa : proximo }));
      modal.success(proximo ? "Indicação pela web ligada." : "Indicação pela web desligada.");
    } catch (e) { modal.error(apiError(e)); }
    finally { setSalvandoToggle(false); }
  };

  const copiarLink = async (url) => {
    const ok = await copiarTexto(url);
    if (ok) modal.success("Link copiado.");
    else modal.error("Não foi possível copiar. Selecione o link e copie manualmente.");
  };

  const baixar = async (fn, u, ext) => {
    try { await fn(urlDe(u), nomeArquivoQr(u.unit_nome, ext)); }
    catch (_) { modal.error("Não foi possível gerar o QR Code."); }
  };

  const imprimir = async (u) => {
    try {
      await imprimirCartaz({ unidadeNome: u.unit_nome, url: urlDe(u), logoUrl: new URL(logo, origem || "http://localhost").href });
    } catch (_) { modal.error("Não foi possível abrir a impressão do cartaz."); }
  };

  // ---------------------------------------------------------------- registros
  const qsLogs = () => {
    const p = new URLSearchParams();
    if (unitParam) p.set("unit_id", unitParam);
    if (categoria !== "all") p.set("categoria", categoria);
    if (evento !== "all") p.set("evento", evento);
    if (resultado !== "all") p.set("sucesso", resultado);
    if (ini) p.set("inicio", ini);
    if (fi) p.set("fim", fi);
    return p.toString();
  };
  const seqLogs = useRef(0);
  const carregarLogs = async (page = 1) => {
    const minha = ++seqLogs.current;
    try {
      const r = await masterApi.get(comPagina(`/master/indicacao-web/logs?${qsLogs()}`, page));
      if (minha !== seqLogs.current) return;
      const pg = normalizaPaginado(r.data, page);
      setLogs({
        items: pg.items, total: pg.total, page: pg.page, total_pages: pg.total_pages,
        totais_por_evento: r?.data?.totais_por_evento || {}, eventos: r?.data?.eventos || null,
      });
    } catch (e) { if (minha === seqLogs.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (periodoValido) carregarLogs(1); }, [unitId, categoria, evento, resultado, inicio, fim]);

  const rotulosEventos = logs.eventos && typeof logs.eventos === "object" ? logs.eventos : EVENTOS_PADRAO;
  const totaisEvento = Object.entries(logs.totais_por_evento || {}).filter(([, n]) => Number(n) > 0);
  const excelUrl = `/master/indicacao-web/logs/excel?${qsLogs()}`;

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-indicacao-web">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Gestão</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Indicação pela web</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Página pública onde o cliente informa a placa e vê na tela os próprios códigos de indicação, com Encaminhar
          no WhatsApp (Cliente Normal ou Motorista de App); quem não tem cadastro faz o autocadastro e confirma o
          celular pelo código enviado, e depois segue o mesmo caminho. O link ou QR Code de qualquer unidade atende clientes de todas e os códigos valem em
          qualquer unidade. O prêmio mostrado é o do Master de cada unidade e categoria. Sem acesso ao sistema.
        </p>
      </div>

      {erroCfg && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] text-rose-800" data-testid="iw-erro-config">
          {erroCfg}
        </div>
      )}

      {/* Liga/desliga */}
      <section className="card-soft rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="iw-toggle-card">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center shrink-0">
            <QrCode size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">Página pública de indicação</span>
              {cfg && (
                <span data-testid="iw-situacao"
                      className={`chip text-[10px] py-0.5 font-bold uppercase tracking-wide ${cfg.ativa ? "chip-green" : "chip-gray"}`}
                      style={{ borderRadius: 6 }}>
                  {cfg.ativa ? "Ligada" : "Desligada"}
                </span>
              )}
            </div>
            <div className="text-[11px] text-neutral-500">
              Vale para todas as unidades. Nasce desligada. Também fica indisponível com a indicação desligada nos descontos.
            </div>
          </div>
        </div>
        <Switch data-testid="iw-toggle" checked={!!cfg?.ativa} disabled={!cfg || salvandoToggle}
                onCheckedChange={(v) => alternar(!!v)} />
      </section>

      {cfg && (
        <div className="flex flex-wrap gap-2 text-[11px]" data-testid="iw-ambiente">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${cfg.captcha_configurado ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-neutral-200 bg-white text-neutral-600"}`}
                data-testid="iw-captcha">
            <ShieldCheck size={12} /> Verificação de segurança (Turnstile): {cfg.captcha_configurado ? "ligada" : "não configurada no servidor"}
          </span>
          {cfg.teto_sms_dia != null && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white text-neutral-600" data-testid="iw-teto">
              Limite de SMS da página: {cfg.teto_sms_dia} por dia
            </span>
          )}
          {cfg.aviso_risco && (
            <span className="inline-flex items-start gap-1 px-2 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-900"
                  data-testid="iw-aviso-risco">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {cfg.aviso_risco}
            </span>
          )}
          {cfg.base_url_configurada === false && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800" data-testid="iw-base-url">
              <AlertTriangle size={12} /> Endereço público não configurado no servidor: o link usa o endereço deste painel
            </span>
          )}
        </div>
      )}

      {/* Unidades: link, QR e texto do WhatsApp */}
      <section className="space-y-3" data-testid="iw-unidades">
        {visiveis.map((u) => {
          const url = urlDe(u);
          return (
            <div key={u.unit_id} className="card-soft rounded-xl p-4 space-y-3" data-testid={`iw-unidade-${u.unit_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-serif-display text-lg mr-auto">{u.unit_nome}</h2>
                {u.unit_ativa === false && (
                  <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-neutral-100 text-neutral-600"
                        data-testid={`iw-inativa-${u.unit_id}`}>
                    Unidade inativa: o link não abre
                  </span>
                )}
              </div>
              <div className="grid md:grid-cols-[180px_1fr] gap-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-[160px] h-[160px] rounded-lg border border-[var(--line)] bg-white flex items-center justify-center overflow-hidden">
                    {qrs[u.unit_id]
                      ? <img src={qrs[u.unit_id]} alt={`QR Code da unidade ${u.unit_nome}`} className="w-full h-full" data-testid={`iw-qr-${u.unit_id}`} />
                      : <span className="text-[11px] text-neutral-400">Gerando...</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-1 w-full max-w-[200px]">
                    <button type="button" data-testid={`iw-png-${u.unit_id}`} onClick={() => baixar(baixarQrPng, u, "png")}
                            className="btn-ghost h-8 text-[10.5px] font-medium inline-flex items-center justify-center gap-1" style={{ borderRadius: 8 }}>
                      <Download size={11} /> PNG
                    </button>
                    <button type="button" data-testid={`iw-svg-${u.unit_id}`} onClick={() => baixar(baixarQrSvg, u, "svg")}
                            className="btn-ghost h-8 text-[10.5px] font-medium inline-flex items-center justify-center gap-1" style={{ borderRadius: 8 }}>
                      <Download size={11} /> SVG
                    </button>
                    <button type="button" data-testid={`iw-imprimir-${u.unit_id}`} onClick={() => imprimir(u)}
                            className="btn-ghost h-8 text-[10.5px] font-medium inline-flex items-center justify-center gap-1" style={{ borderRadius: 8 }}
                            title="Cartaz A4 para impressão">
                      <Printer size={11} /> Cartaz
                    </button>
                  </div>
                </div>
                <div className="space-y-3 min-w-0">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-neutral-500">Link público</label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <input readOnly value={url} data-testid={`iw-link-${u.unit_id}`} onFocus={(e) => e.target.select()}
                             className="flex-1 min-w-[180px] h-10 rounded-lg bg-neutral-50 border border-neutral-200 px-3 text-[12.5px] font-mono" />
                      <button type="button" data-testid={`iw-copiar-${u.unit_id}`} onClick={() => copiarLink(url)}
                              className="btn-ghost h-10 px-3 text-[11.5px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
                        <Copy size={12} /> Copiar link
                      </button>
                      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`iw-abrir-${u.unit_id}`}
                         className="btn-ghost h-10 px-3 text-[11.5px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
                        <ExternalLink size={12} /> Abrir
                      </a>
                    </div>
                  </div>
                  {/* Fase 5 (19/10, item 7): o texto do wa.me saiu daqui para a central de mensagens
                      (padrão do sistema e texto por unidade; o texto já salvo de cada unidade foi migrado). */}
                  <div className="rounded-lg border border-dashed border-neutral-300 bg-white/60 px-3 py-2.5 space-y-1.5"
                       data-testid={`iw-texto-central-${u.unit_id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                      <MessageCircle size={11} /> Texto do "Encaminhar no WhatsApp"
                    </div>
                    <p className="text-[11.5px] text-neutral-600">
                      O texto (com {VARIAVEIS_TEXTO.map((v) => `{${v}}`).join(", ")}) fica em Sistema &gt; Mensagens automáticas,
                      no padrão do sistema ou só desta unidade. Sem emojis.
                    </p>
                    <Link to={`/master/mensagens?unidade=${encodeURIComponent(u.unit_id)}&chave=indicacao_web_wame`} data-testid={`iw-texto-link-${u.unit_id}`}
                          className="btn-ghost h-9 px-3 text-[12px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
                      <MessagesSquare size={13} /> Editar o texto
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {cfg && visiveis.length === 0 && (
          <div className="card-soft rounded-xl p-5 text-center text-[12px] text-neutral-500" data-testid="iw-sem-unidades">
            Nenhuma unidade encontrada{unitParam ? " para a unidade escolhida no topo" : ""}.
          </div>
        )}
        {!cfg && !erroCfg && <div className="text-center text-[12px] text-neutral-500 py-4">Carregando...</div>}
      </section>

      {/* Registros */}
      <section className="space-y-2" data-testid="iw-logs">
        <div className="flex items-center gap-2">
          <ScrollText size={15} className="text-[color:var(--brand-blue)]" />
          <h2 className="font-serif-display text-lg mr-auto">Registros da página</h2>
          <ExcelBtn testid="iw-logs-excel" url={excelUrl} filename="indicacao_web_registros.xlsx" />
        </div>
        <p className="text-[11px] text-neutral-500">
          Unidade: a escolhida no topo da tela. Categoria do cliente, período (dia de Brasília), evento e resultado nos filtros abaixo.
        </p>
        <div className="card-soft rounded-xl p-3 flex flex-wrap gap-3 items-end" data-testid="iw-filtros">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">De</label>
            <input type="date" data-testid="iw-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                   className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">até</label>
            <input type="date" data-testid="iw-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                   className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
          </div>
          <div className="flex flex-wrap gap-1">
            {ATALHOS.map(([k, lbl]) => (
              <button key={k} type="button" data-testid={`iw-atalho-${k}`}
                      onClick={() => { const a = atalhoPeriodoBR(k); setInicio(a.inicio); setFim(a.fim); }}
                      className={`h-9 px-2.5 text-[11px] font-medium ${atalhoAtivo === k ? "btn-primary-blue" : "btn-ghost"}`}
                      style={{ borderRadius: 8 }}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="min-w-[160px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Categoria</label>
            <select data-testid="iw-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} className={inputCls}>
              {CATEGORIAS_LOG.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
          </div>
          <div className="min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Evento</label>
            <select data-testid="iw-evento" value={evento} onChange={(e) => setEvento(e.target.value)} className={inputCls}>
              <option value="all">Todos os eventos</option>
              {Object.entries(rotulosEventos).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Resultado</label>
            <select data-testid="iw-resultado" value={resultado} onChange={(e) => setResultado(e.target.value)} className={inputCls}>
              {RESULTADOS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
          </div>
          {!periodoValido && (
            <div className="text-[12px] text-rose-700 w-full" data-testid="iw-periodo-invalido">
              Informe as duas datas, com o início antes do fim.
            </div>
          )}
        </div>

        {totaisEvento.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="iw-totais">
            {totaisEvento.map(([k, n]) => (
              <span key={k} data-testid={`iw-total-${k}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white text-[11px] text-neutral-700">
                {rotulosEventos[k] || k}: <b>{n}</b>
              </span>
            ))}
          </div>
        )}

        <div className="card-soft rounded-xl overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Data/hora</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Evento</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Resultado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">IP</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Detalhe</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logs.items.map((l, i) => (
                <TableRow key={l.id || `${l.criado_em}-${i}`} data-testid={`iw-log-${l.id || i}`}>
                  <TableCell className="text-[12px] whitespace-nowrap">{fmtDataHoraBR(l.criado_em)}</TableCell>
                  <TableCell className="text-[12px]">{l.unit_nome || "—"}</TableCell>
                  <TableCell className="text-[12px]">{l.cliente_nome || "—"}</TableCell>
                  <TableCell className="text-[12px]">{CATEGORIA_LOG_LBL[l.categoria] || l.categoria || "—"}</TableCell>
                  <TableCell className="text-[12px]">{l.evento_label || rotulosEventos[l.evento] || l.evento}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${l.sucesso === false ? "bg-rose-100 text-rose-800" : "bg-emerald-50 text-emerald-700"}`}>
                      {l.sucesso === false ? "Falha" : "Sucesso"}
                    </span>
                  </TableCell>
                  <TableCell className="text-[11.5px] font-mono whitespace-nowrap">{l.ip || "—"}</TableCell>
                  <TableCell className="text-[11.5px] text-neutral-600 max-w-[260px] truncate" title={l.detalhe || ""}>{l.detalhe || "—"}</TableCell>
                </TableRow>
              ))}
              {logs.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-neutral-500 py-8">Nenhum registro no período.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Pagination page={logs.page || 1} totalPages={logs.total_pages || 0} total={logs.total || 0}
                    onPage={carregarLogs} unitLabel="registros" />
      </section>
    </div>
  );
}
